import React, { useContext, useState, useCallback, useEffect } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { CircularProgress, Tab, Tabs, Typography } from "@mui/material";

import { GlobalContext } from "@/helpers/contexts/contexts";
import { BRAND_CONST } from "@/config/constants";
import { ROUTES } from "@/helpers/routes/routePaths";
import PageLayout from "@/components/layouts/page/PageLayout";
import FullScreenLoader from "@/components/loaders/FullScreenLoader";
import { DigitalCampaignList } from "@/features/campaigns/digital/digital-campaign-list";
import { DirectMailCampaignList } from "@/features/campaigns/direct-mail/direct-mail-campaign-list";
import CampaignOverviewFilters from "../components/CampaignOverviewFilters";
import useDigitalCampaignOverview from "../hooks/useDigitalCampaignOverview";
import useDirectMailCampaignOverview from "../hooks/useDirectMailCampaignOverview";
import NoDataFoundBase from "@/components/NoDataFound";
import useFetcher from "@/helpers/hooks/useFetcher";
import { campaignOverviewService } from "../services/campaignOverviewService";
import { S3_BUCKET_URL } from "@/config/appConfig";

const TABS = { DIGITAL: 0, DIRECT_MAIL: 1 };
const NoDataFound = NoDataFoundBase as React.ComponentType<any>;

const INITIAL_FILTERS = {
  search: "",
  tags: [],
  channels: [],
  groups: [],
  participant: null,
};

const LoadingMore = () => (
  <div className="text-center bg-light mt-2">
    <Typography variant="caption" color="text.secondary">
      <CircularProgress color="primary" size={10} className="mr-1" /> Loading
      ...
    </Typography>
  </div>
);

type ParticipantFilterValue = {
  id: string | number;
  systemId?: string | number;
  name?: string;
  avatar?: string | null;
};

type OverviewFilters = {
  search: string;
  tags: Array<Record<string, any>>;
  channels: Array<Record<string, any>>;
  groups: Array<string | number>;
  participant: ParticipantFilterValue | null;
};

type CampaignTabProps = {
  filters: OverviewFilters;
  isParticipantsLoading: boolean;
  onLoadingChange?: () => void;
};

const DigitalTab = ({
  filters,
  isParticipantsLoading,
  onLoadingChange,
}: CampaignTabProps) => {
  const { loggedInUser, selectedBrand } = useContext(GlobalContext);
  const { campaigns, isLoading, isValidating, errorMessage, containerId } =
    useDigitalCampaignOverview({
      filters,
      selectedBrand,
      loggedInUser,
      isParticipantsLoading,
    });

  useEffect(() => {
    if (!isLoading) onLoadingChange?.();
  }, [isLoading, onLoadingChange]);

  return (
    <div id={containerId}>
      {errorMessage ? (
        <NoDataFound className="my-5" message={errorMessage} />
      ) : (
        <DigitalCampaignList
          campaigns={campaigns}
          isLoading={isLoading}
          searchValue={filters.search}
          participantId={filters.participant?.id}
        />
      )}
      {!isLoading && isValidating && campaigns.length > 0 && <LoadingMore />}
    </div>
  );
};

const DirectMailTab = ({
  filters,
  isParticipantsLoading,
  onLoadingChange,
}: CampaignTabProps) => {
  const { loggedInUser, selectedBrand } = useContext(GlobalContext);
  const { campaigns, isLoading, isValidating, errorMessage, containerId } =
    useDirectMailCampaignOverview({
      filters,
      selectedBrand,
      loggedInUser,
      isParticipantsLoading,
    });

  useEffect(() => {
    if (!isLoading) onLoadingChange?.();
  }, [isLoading, onLoadingChange]);

  return (
    <div id={containerId}>
      {errorMessage ? (
        <NoDataFound className="my-5" message={errorMessage} />
      ) : (
        <DirectMailCampaignList
          campaigns={campaigns}
          isLoading={isLoading}
          searchValue={filters.search}
          participantId={filters.participant?.id}
        />
      )}
      {!isLoading && isValidating && campaigns.length > 0 && <LoadingMore />}
    </div>
  );
};

const CampaignOverviewPage = () => {
  const { selectedBrand } = useContext(GlobalContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab =
    searchParams.get("tab") === "direct-mail" ? TABS.DIRECT_MAIL : TABS.DIGITAL;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [isTabSwitching, setIsTabSwitching] = useState(false);

  const brandId = selectedBrand?.id;
  const participantIdParam = searchParams.get("participantId");

  const { data: participantsData, isLoading: isParticipantsLoading } =
    useFetcher(
      brandId ? { brand_id: brandId } : null,
      campaignOverviewService.getFilterParticipants,
      { revalidateOnFocus: false }
    );

  const participantsResponse = participantsData as any;
  const participants = (participantsResponse?.result || []).map((p: any) => ({
    id: p.id,
    systemId: p.system_generated_participant_id,
    name: p.display_name,
    avatar: p.profile_name ? `${S3_BUCKET_URL}${p.profile_name}` : null,
  }));

  // When participants list resolves (initial load or brand change), use the
  // URL participant when present so browser Back can restore the filter.
  useEffect(() => {
    if (isParticipantsLoading) return;
    const urlParticipant = participantIdParam
      ? participants.find((p) => String(p.id) === participantIdParam)
      : null;
    const participant = urlParticipant ?? participants[0] ?? null;
    setFilters({ ...INITIAL_FILTERS, participant });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantsData, participantIdParam]);

  const hasParticipants = !isParticipantsLoading && participants.length > 0;

  // Only pass participant to filters/hooks once confirmed in current list.
  const validParticipantId = filters.participant?.id;
  const isParticipantValid =
    !isParticipantsLoading &&
    participants.some((p) => p.id === validParticipantId);

  const safeFilters = isParticipantValid
    ? filters
    : { ...filters, participant: null };

  const handleFilterChange = useCallback(
    (key: string, value: any) => {
      setFilters((prev) => ({ ...prev, [key]: value }));

      if (key !== "participant") return;

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value?.id) {
            next.set("participantId", value.id);
          } else {
            next.delete("participantId");
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, newTab: number) => {
      setActiveTab(newTab);
      setIsTabSwitching(!!filters.participant);
      setFilters((prev) => {
        if (!prev.participant) return { ...INITIAL_FILTERS };
        return { ...INITIAL_FILTERS, participant: prev.participant };
      });
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (newTab === TABS.DIRECT_MAIL) {
            next.set("tab", "direct-mail");
          } else {
            next.delete("tab");
          }
          return next;
        },
        { replace: true }
      );
    },
    [filters.participant, setSearchParams]
  );

  const handleLoadingChange = useCallback(() => {
    setIsTabSwitching(false);
  }, []);

  if (
    selectedBrand?.id === BRAND_CONST.MS_BRAND_ID ||
    selectedBrand?.id === BRAND_CONST.KUMON_BRAND_ID
  ) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return (
    <PageLayout className="participant-dashboard-container mx-0 mx-sm-2">
      <div className="dashboard pb-5">
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          slotProps={{
            indicator: { sx: { display: "none" } },
          }}
          sx={{
            minHeight: 40,
            width: "fit-content",
            "& .MuiTabs-flexContainer": {
              gap: 1.5,
            },
            "& .MuiTab-root": {
              minHeight: 36,
              minWidth: 124,
              borderRadius: "var(--radius-control)",
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "var(--neutral-white)",
              px: 3,
              py: 0,
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.75rem",
              color: "text.primary",
              transition: "background 0.2s, border-color 0.2s, color 0.2s",
            },
            "& .Mui-selected": {
              bgcolor: "primary.main",
              borderColor: "primary.main",
              color: "var(--palette-primary-contrastText) !important",
            },
          }}
        >
          <Tab
            label="Digital Campaigns"
            value={TABS.DIGITAL}
            disabled={!hasParticipants}
          />
          <Tab
            label="Direct Mail"
            value={TABS.DIRECT_MAIL}
            disabled={!hasParticipants}
          />
        </Tabs>

        {hasParticipants && (
          <CampaignOverviewFilters
            filters={safeFilters}
            participants={participants}
            isParticipantsLoading={isParticipantsLoading}
            onFilterChange={handleFilterChange}
            activeTab={activeTab}
          />
        )}

        <FullScreenLoader
          isActive={isParticipantsLoading || isTabSwitching}
          whiteBg
        />

        <div className="mt-3">
          {!isParticipantsLoading && !hasParticipants ? (
            <NoDataFound message="No participants found. Please add participants to view campaigns." />
          ) : (
            <>
              {activeTab === TABS.DIGITAL && (
                <DigitalTab
                  filters={safeFilters}
                  isParticipantsLoading={isParticipantsLoading}
                  onLoadingChange={handleLoadingChange}
                />
              )}
              {activeTab === TABS.DIRECT_MAIL && (
                <DirectMailTab
                  filters={safeFilters}
                  isParticipantsLoading={isParticipantsLoading}
                  onLoadingChange={handleLoadingChange}
                />
              )}
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default CampaignOverviewPage;
